import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
});

// Add interceptor for token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalReq = error.config;
    // Skip retry for auth check endpoints and already retried requests
    const isAuthCheck = originalReq?.url?.includes('/auth/me') || originalReq?.url?.includes('/auth/refresh');
    if (error.response?.status === 401 && !originalReq._retry && !isAuthCheck) {
      originalReq._retry = true;
      try {
        const { data } = await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          originalReq.headers.Authorization = `Bearer ${data.access_token}`;
          return api(originalReq);
        }
      } catch {
        localStorage.removeItem('access_token');
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const { data } = await api.post('/auth/login', { email, password, remember_me: rememberMe });
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    localStorage.removeItem('access_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
